import { FormEvent, useState } from 'react';
import { dealerErrorCode, dealerErrorMessage, dealersApi, isFeatureOff } from '../../services/dealersApi';
import { CreditRequest } from '../../types/dealers';
import { describedBy, FormField } from '../promotions/PromoFormParts';
import { aed } from './dealerFormat';

const PHRASE = 'REVOKE';

interface RevokeDialogProps {
  dealerId: string;
  request: CreditRequest;
  token: string;
  onClose: () => void;
  onRevoked: (saved: CreditRequest) => void;
  /** 409 CREDIT_LIVE_IMMUTABLE: it was paid meanwhile; the caller reloads. */
  onStale: (message: string) => void;
  onFeatureOff: () => void;
}

/**
 * Spec 085 Flow E step 3 — revoke an unpaid request with a mandatory reason. The request and its
 * Pending credit become REVOKED, the proforma is voided, dealer and buyer are told. Paid credit is
 * never revoked here (BR-15). Same typed confirmation as `TypedConfirmDialog`, plus the reason.
 */
export default function RevokeDialog({ dealerId, request, token, onClose, onRevoked, onStale, onFeatureOff }: RevokeDialogProps) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const ready = reason.trim().length > 0 && typed === PHRASE;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || saving) return;
    setError(null);
    setSaving(true);
    try {
      onRevoked(await dealersApi.revokeRequest(dealerId, request.id, reason.trim(), token));
    } catch (err) {
      if (isFeatureOff(err)) { onFeatureOff(); onClose(); return; }
      if (dealerErrorCode(err) === 'CREDIT_LIVE_IMMUTABLE') { onStale(dealerErrorMessage(err)); return; }
      setError(dealerErrorMessage(err, 'Failed to revoke the request'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal admin-modal-sm" role="dialog" aria-modal="true" aria-label={`Revoke ${request.number ?? 'request'}?`}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>Revoke {request.number ?? 'request'}?</h3>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="admin-modal-body">
            {error && <div className="admin-info-banner danger" role="alert" data-testid="revoke-error">{error}</div>}
            <p>
              The {request.kind === 'PACK' ? 'pack order' : `${aed(request.faceValueAed)} pending credit`} is cancelled and its proforma voided.
              The dealer{request.kind === 'PACK' ? '' : ' and the buyer'} are told. This cannot be undone.
            </p>
            <FormField id="revoke-reason" label="Reason (sent to the dealer)">
              <textarea id="revoke-reason" className="admin-form-input" rows={3} maxLength={500} value={reason} disabled={saving}
                {...describedBy('revoke-reason')} onChange={(e) => setReason(e.target.value)} />
            </FormField>
            <p>To continue, type <code className="admin-typed-confirm-phrase">{PHRASE}</code> below:</p>
            <input className="admin-typed-confirm-input" value={typed} disabled={saving} aria-label={`Type ${PHRASE} to confirm`}
              onChange={(e) => setTyped(e.target.value)} />
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-danger" disabled={!ready || saving}>{saving ? 'Revoking…' : 'Revoke'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
